import _values from 'lodash/values';
// Action types
import * as types from '../constants/ActionTypes';
// Models
import mAuth from '../models/auth';
import mProduct from '../models/product';

// From product, get if GraphQL API is used
const { useGraphQL } = mProduct.getConfig();

// Reinit state
export function reinitState(target) {
  return { type: types.REINIT_STATE, target };
}

// Persist / versionning
export function storePersistor(persistor) {
  return { type: types.STORE_PERSISTOR, persistor };
}
export function updatePersistStoreVersion(version) {
  return { type: types.UPDATE_PERSIST_STORE_VERSION, version };
}

// Loading generic actions
export function startLoading() {
  return { type: types.START_LOADING };
}
export function endLoading() {
  return { type: types.END_LOADING };
}
export function hideLoading() {
  return { type: types.HIDE_LOADING };
}
export function hideLoadingReinit() {
  return { type: types.HIDE_LOADING_REINIT };
}

// Locale
export function changeLocale(locale) {
  return { type: types.CHANGE_LOCALE, locale };
}

// Notifications
export function addNotification(notification) {
  return { type: types.ADD_NOTIFICATION, notification };
}
export function deleteNotification(id) {
  return { type: types.DELETE_NOTIFICATION, id };
}

// Dialogs
export function openDialog(props, closeOthers = false) {
  return { type: types.OPEN_DIALOG, props, closeOthers };
}
export function closeDialog(componentType) {
  return { type: types.CLOSE_DIALOG, componentType };
}
export function flushDialog(componentType) {
  return { type: types.FLUSH_DIALOG, componentType };
}

// WebSocket
export function wsConnect() {
  return { type: types.WS_CONNECT };
}
export function wsDisconnect() {
  return { type: types.WS_DISCONNECT };
}
export function wsJoinTopic(topic, ref, payload, joinRef) {
  return {
    type: types.WS_JOIN_TOPIC, topic, ref, payload, joinRef,
  };
}
export function wsLeaveTopic(topic, ref) {
  return { type: types.WS_LEAVE_TOPIC, topic, ref };
}
export function wsSendMessage(topic, event, payload, ref) {
  return {
    type: types.WS_SEND_MESSAGE, topic, event, payload, ref,
  };
}
export function wsSendSubscription(joinRef, subscription, data) {
  return {
    type: types.WS_SEND_SUBSCRIPTION, joinRef, subscription, data,
  };
}

// Authentication
// `useGraphQL` is injected as parameter for tests purpose
export function reinitAuth() {
  return { type: types.REINIT_AUTH };
}
export function postLogin(params, localUseGraphQL = useGraphQL) {
  let data = { ...params };
  if (localUseGraphQL) {
    data = { query: mAuth.mutDoLogin, data: { ...params } };
  }
  return { type: types.POST_LOGIN, useGraphQL: localUseGraphQL, data };
}
export function postLoginByMagicLink(query, data) {
  return {
    type: types.POST_LOGIN_BY_MAGIC_LINK, query, data,
  };
}
export function postLogout(localUseGraphQL = useGraphQL) {
  let data;
  if (localUseGraphQL) {
    data = { query: mAuth.mutDoLogout };
  }
  return { type: types.POST_LOGOUT, useGraphQL: localUseGraphQL, data };
}
export function login() {
  return { type: types.LOGIN };
}
export function logout() {
  return { type: types.LOGOUT };
}
export function postRecoverPassword(params, localUseGraphQL = useGraphQL) {
  let data = { ...params };
  if (localUseGraphQL) {
    data = { query: mAuth.mutCreatePasswordRecoveryLink, data: { ...params } };
  }
  return { type: types.POST_RECOVER_PASSWORD, useGraphQL: localUseGraphQL, data };
}
export function getRecoverPasswordLink(params, localUseGraphQL = useGraphQL) {
  let data = { ...params };
  if (localUseGraphQL) {
    data = { query: mAuth.queCheckPasswordRecoveryLink, data: { ...params } };
  }
  return { type: types.GET_RECOVER_PASSWORD_LINK, useGraphQL: localUseGraphQL, data };
}
export function updateRecoverPasswordLink(params, localUseGraphQL = useGraphQL) {
  let data = { ...params };
  if (localUseGraphQL) {
    data = { query: mAuth.mutUsePasswordRecoveryLink, data: { ...params } };
  }
  return { type: types.UPDATE_RECOVER_PASSWORD_LINK, useGraphQL: localUseGraphQL, data };
}

// Me
// `useGraphQL` is injected as parameter for tests purpose
export function getMe(localUseGraphQL = useGraphQL) {
  let data;
  if (localUseGraphQL) {
    data = { query: mAuth.queGetMe };
  }
  return { type: types.GET_ME, useGraphQL: localUseGraphQL, data };
}
export function updateMe(data, localUseGraphQL = useGraphQL) {
  // Note that data contains account fields (email, ...) encapsulated
  // in a `user` key (@see user model serializeUser method)
  // In GraphQL, we don't send this key, but only its value (_values(data)[0])
  return {
    type: types.UPDATE_ME,
    useGraphQL: localUseGraphQL,
    data: localUseGraphQL ? { query: mAuth.mutUpdateMe, data: _values(data)[0] } : data,
  };
}

export function cpmDeleteMyPaymentMethod(query, data) {
  return { type: types.CPM_DELETE_MY_PAYMENT_METHOD, query, data };
}

// List Param
export function refreshList() {
  return { type: types.REFRESH_LIST };
}
export function refreshListReinit() {
  return { type: types.REFRESH_LIST_REINIT };
}
export function updateListLastParams(target, reinit, pagination, sort, filters) {
  return {
    type: types.UPDATE_LIST_LAST_PARAMS, target, reinit, pagination, sort, filters,
  };
}

// Async operation
export function registerAsyncOperation(asyncOperation) {
  return { type: types.REGISTER_ASYNC_OPERATION, asyncOperation };
}
export function purgeAsyncOperations(targetKey) {
  return { type: types.PURGE_ASYNC_OPERATIONS, targetKey };
}
export function timeoutAsyncOperation(asyncOperationId) {
  return { type: types.TIMEOUT_ASYNC_OPERATION, asyncOperationId };
}

// Registrations
export function registerDeviceCmd(device, cmd, asyncOperationId) {
  return {
    type: types.REGISTER_DEVICE_CMD, device, cmd, asyncOperationId,
  };
}
export function registerInstallationTrigger(resource, asyncOperationId) {
  return { type: types.REGISTER_INSTALLATION_TRIGGER, resource, asyncOperationId };
}

// AutoComplete
export function getInstallationsAutocomplete(pagination, sort, filters) {
  return {
    type: types.GET_INSTALLATIONS_AUTOCOMPLETE, pagination, sort, filters,
  };
}
export function getInstallationCustomersAutocomplete(id, pagination, sort, filters) {
  return {
    type: types.GET_INSTALLATION_CUSTOMERS_AUTOCOMPLETE, id, pagination, sort, filters,
  };
}
export function getDevicesAutocomplete(pagination, sort, filters) {
  return {
    type: types.GET_DEVICES_AUTOCOMPLETE, pagination, sort, filters,
  };
}
export function getSimcardsAutocomplete(pagination, sort, filters) {
  return {
    type: types.GET_SIMCARDS_AUTOCOMPLETE, pagination, sort, filters,
  };
}
export function getDistributionsAutocomplete(pagination, sort, filters) {
  return {
    type: types.GET_DISTRIBUTIONS_AUTOCOMPLETE, pagination, sort, filters,
  };
}
export function getProfilesAutocomplete(pagination, sort, filters) {
  return {
    type: types.GET_PROFILES_AUTOCOMPLETE, pagination, sort, filters,
  };
}

// List filter
export function getInstallationLF(id) {
  return { type: types.GET_INSTALLATION_LF, id };
}
export function getInstallationCustomerLF(id, idCustomer) {
  return { type: types.GET_INSTALLATION_CUSTOMER_LF, id, idCustomer };
}
export function getDeviceLF(id) {
  return { type: types.GET_DEVICE_LF, id };
}
export function getSimcardLF(id) {
  return { type: types.GET_SIMCARD_LF, id };
}
export function getDistributionLF(id) {
  return { type: types.GET_DISTRIBUTION_LF, id };
}
export function getProfileLF(id) {
  return { type: types.GET_PROFILE_LF, id };
}

// Operators
export function getOperators() {
  return { type: types.GET_OPERATORS };
}

// Equipments
export function getEquipmentsList(pagination, sort, filters) {
  return {
    type: types.GET_EQUIPMENTS_LIST, pagination, sort, filters,
  };
}

// Device events
export function addDeviceEvent(eventType, status, data) {
  return {
    type: types.ADD_DEVICE_EVENT, eventType, status, data,
  };
}
export function updateDeviceEventStatus(id, status) {
  return { type: types.UPDATE_DEVICE_EVENT_STATUS, id, status };
}

// Device models
export function getDeviceModels() {
  return { type: types.GET_DEVICE_MODELS };
}

// Devices
// Main
export function getDevicesList(pagination, sort, filters, updateDeviceData = true) {
  return {
    type: types.GET_DEVICES_LIST, pagination, sort, filters, cbParams: { updateDeviceData },
  };
}
export function getDevice(id, updateDeviceData = true) {
  return { type: types.GET_DEVICE, id, cbParams: { updateDeviceData } };
}
export function getDeviceInfo(id) {
  return { type: types.GET_DEVICE_INFO, id };
}
export function getDeviceSync(id) {
  return { type: types.GET_DEVICE_SYNC, id, cbParams: { id } };
}
export function getDeviceInstallation(idInstallation) {
  return { type: types.GET_DEVICE_INSTALLATION, idInstallation };
}
export function linkDeviceSimcard(id, data) {
  return { type: types.LINK_DEVICE_SIMCARD, id, data };
}
export function unlinkDeviceSimcard(id, data) {
  return { type: types.UNLINK_DEVICE_SIMCARD, id, data };
}
export function sendCmdDevice(id, device, cmd) {
  return {
    type: types.SEND_CMD_DEVICE, id, cmd, cbParams: { device, cmd },
  };
}
export function deleteDevice(id) {
  return { type: types.DELETE_DEVICE, id };
}
export function updateDevice(id, data) {
  return { type: types.UPDATE_DEVICE, id, data };
}
// SMS
export function sendDeviceSms(id, device, sms, smsType) {
  return {
    type: types.SEND_DEVICE_SMS, id, sms, cbParams: { device, smsType },
  };
}
export function getDeviceSmsHistory(id) {
  return { type: types.GET_DEVICE_SMS_HISTORY, id };
}
export function getDeviceSmsStatus(id, idSms) {
  return { type: types.GET_DEVICE_SMS_STATUS, id, idSms };
}
// Config
export function updateDeviceConfig(id, idConfig, data) {
  return {
    type: types.UPDATE_DEVICE_CONFIG, id, idConfig, data,
  };
}
// Logs
export function getDeviceLogsList(id, pagination, sort, filters, desynchronized) {
  return {
    type: types.GET_DEVICE_LOGS_LIST, id, pagination, sort, filters, desynchronized,
  };
}
// Distributions
export function getDeviceDistributionsHistory(id) {
  return { type: types.GET_DEVICE_DISTRIBUTIONS_HISTORY, id };
}

// Device slaves
// Main
export function getDeviceSlavesList(pagination, sort, filters, updateDeviceData = true) {
  return {
    type: types.GET_DEVICE_SLAVES_LIST, pagination, sort, filters, cbParams: { updateDeviceData },
  };
}
export function getDeviceSlave(id, updateDeviceData = true) {
  return { type: types.GET_DEVICE_SLAVE, id, cbParams: { updateDeviceData } };
}
export function deleteDeviceSlave(id) {
  return { type: types.DELETE_DEVICE_SLAVE, id };
}
export function updateDeviceSlave(id, data) {
  return { type: types.UPDATE_DEVICE_SLAVE, id, data };
}

// Installations
// Cache
export function resetInstallationCache() {
  return { type: types.RESET_INSTALLATION_CACHE };
}
// Main
export function getInstallationsList(pagination, sort, filters, extraParams, updateDeviceData = true) {
  return {
    type: types.GET_INSTALLATIONS_LIST, pagination, sort, filters, extraParams, cbParams: { updateDeviceData },
  };
}
export function addInstallation(data) {
  return { type: types.ADD_INSTALLATION, data };
}
export function getInstallation(id, updateDeviceData = true) {
  return { type: types.GET_INSTALLATION, id, cbParams: { updateDeviceData } };
}
export function setInstallationPermissions(permissions) {
  return { type: types.SET_INSTALLATION_PERMISSIONS, permissions };
}
export function updateInstallation(id, data) {
  return { type: types.UPDATE_INSTALLATION, id, data };
}
export function deleteInstallation(id) {
  return { type: types.DELETE_INSTALLATION, id };
}
// Entities
export function getInstallationEntites(id) {
  return { type: types.GET_INSTALLATION_ENTITIES, id };
}
export function addInstallationEntity(id, data) {
  return { type: types.ADD_INSTALLATION_ENTITY, id, data };
}
export function deleteInstallationEntity(id, idEntity) {
  return { type: types.DELETE_INSTALLATION_ENTITY, id, idEntity };
}
export function updateInstallationEntity(id, idEntity, data) {
  return {
    type: types.UPDATE_INSTALLATION_ENTITY, id, idEntity, data,
  };
}
// Doors
export function getInstallationDoors(id) {
  return { type: types.GET_INSTALLATION_DOORS, id };
}
export function addInstallationDoor(id, data) {
  return { type: types.ADD_INSTALLATION_DOOR, id, data };
}
export function deleteInstallationDoor(id, idDoor) {
  return { type: types.DELETE_INSTALLATION_DOOR, id, idDoor };
}
export function getInstallationDoor(id, idDoor) {
  return { type: types.GET_INSTALLATION_DOOR, id, idDoor };
}
export function updateInstallationDoor(id, idDoor, data) {
  return {
    type: types.UPDATE_INSTALLATION_DOOR, id, idDoor, data,
  };
}
// Sources
export function getInstallationSources(id) {
  return { type: types.GET_INSTALLATION_SOURCES, id };
}
export function addInstallationSource(id, data) {
  return { type: types.ADD_INSTALLATION_SOURCE, id, data };
}
export function deleteInstallationSource(id, idSource) {
  return { type: types.DELETE_INSTALLATION_SOURCE, id, idSource };
}
export function getInstallationSource(id, idSource) {
  return { type: types.GET_INSTALLATION_SOURCE, id, idSource };
}
export function updateInstallationSource(id, idSource, data) {
  return {
    type: types.UPDATE_INSTALLATION_SOURCE, id, idSource, data,
  };
}
// Devices
export function getInstallationDevices(id, updateDeviceData = true) {
  return { type: types.GET_INSTALLATION_DEVICES, id, cbParams: { updateDeviceData } };
}
export function addInstallationDevice(id, data) {
  return { type: types.ADD_INSTALLATION_DEVICE, id, data };
}
export function deleteInstallationDevice(id, idDevice) {
  return { type: types.DELETE_INSTALLATION_DEVICE, id, idDevice };
}
export function getInstallationDevice(id, idDevice) {
  return { type: types.GET_INSTALLATION_DEVICE, id, idDevice };
}
export function updateInstallationDevice(id, idDevice, data) {
  return {
    type: types.UPDATE_INSTALLATION_DEVICE, id, idDevice, data,
  };
}
export function replaceInstallationDevice(id, idDevice, idNewDevice) {
  return {
    type: types.REPLACE_INSTALLATION_DEVICE, id, idDevice, idNewDevice,
  };
}
// Device slaves
export function getInstallationDeviceSlave(id, idDevice, idSlave) {
  return {
    type: types.GET_INSTALLATION_DEVICE_SLAVE, id, idDevice, idSlave,
  };
}
export function updateInstallationDeviceSlave(id, idDevice, idSlave, data) {
  return {
    type: types.UPDATE_INSTALLATION_DEVICE_SLAVE, id, idDevice, idSlave, data,
  };
}
export function addInstallationDeviceSlave(id, idDevice, data) {
  return {
    type: types.ADD_INSTALLATION_DEVICE_SLAVE, id, idDevice, data,
  };
}
export function deleteInstallationDeviceSlave(id, idDevice, idSlave) {
  return {
    type: types.DELETE_INSTALLATION_DEVICE_SLAVE, id, idDevice, idSlave,
  };
}
export function replaceInstallationDeviceSlave(id, idDevice, idSlave, idNewSlave) {
  return {
    type: types.REPLACE_INSTALLATION_DEVICE_SLAVE, id, idDevice, idSlave, idNewSlave,
  };
}
// Scenarios
export function getInstallationScenarios(id, firstLoad) {
  return { type: types.GET_INSTALLATION_SCENARIOS, id, firstLoad };
}
export function addInstallationScenario(id, data) {
  return { type: types.ADD_INSTALLATION_SCENARIO, id, data };
}
export function deleteInstallationScenario(id, idScenario) {
  return { type: types.DELETE_INSTALLATION_SCENARIO, id, idScenario };
}
export function getInstallationScenario(id, idScenario) {
  return { type: types.GET_INSTALLATION_SCENARIO, id, idScenario };
}
export function updateInstallationScenario(id, idScenario, data) {
  return {
    type: types.UPDATE_INSTALLATION_SCENARIO, id, idScenario, data,
  };
}
export function duplicateInstallationScenario(data) {
  return { type: types.DUPLICATE_SCENARIO, data };
}
export function triggerInstallationScenario(id, idScenario, name) {
  return {
    type: types.TRIGGER_INSTALLATION_SCENARIO, id, idScenario, cbParams: { scenario: { id: idScenario, name } },
  };
}
// Customers
export function getInstallationCustomersList(id, pagination, sort, filters, firstLoad, desynchronized) {
  return {
    type: types.GET_INSTALLATION_CUSTOMERS_LIST, id, pagination, sort, filters, firstLoad, desynchronized,
  };
}
export function getInstallationCustomer(id, idCustomer) {
  return { type: types.GET_INSTALLATION_CUSTOMER, id, idCustomer };
}
export function addInstallationCustomer(id, data) {
  return { type: types.ADD_INSTALLATION_CUSTOMER, id, data };
}
export function updateInstallationCustomer(id, idCustomer, data) {
  return {
    type: types.UPDATE_INSTALLATION_CUSTOMER, id, idCustomer, data,
  };
}
export function updateInstallationCustomerState(id, idCustomer, data) {
  return {
    type: types.UPDATE_INSTALLATION_CUSTOMER_STATE, id, idCustomer, data,
  };
}
export function deleteInstallationCustomer(id, idCustomer) {
  return { type: types.DELETE_INSTALLATION_CUSTOMER, id, idCustomer };
}
export function getInstallationCustomerRandomUniqueKeypadCode(id) {
  return { type: types.GET_INSTALLATION_CUSTOMER_RANDOM_UNIQUE_KEYPAD_CODE, id };
}
export function generateInstallationCustomersDailyCodes(id, data) {
  return { type: types.GENERATE_INSTALLATION_CUSTOMERS_DAILY_CODES, id, data };
}
export function exportInstallationCustomers(id) {
  return { type: types.EXPORT_INSTALLATION_CUSTOMERS, id };
}
// Customer access ways
export function getInstallationCustomerAccessways(id, idCustomer) {
  return { type: types.GET_INSTALLATION_CUSTOMER_ACCESSWAYS, id, idCustomer };
}
export function addInstallationCustomerAccessway(id, idCustomer, data) {
  return {
    type: types.ADD_INSTALLATION_CUSTOMER_ACCESSWAY, id, idCustomer, data,
  };
}
export function updateInstallationCustomerAccessway(id, idCustomer, idAccessWay, data) {
  return {
    type: types.UPDATE_INSTALLATION_CUSTOMER_ACCESSWAY, id, idCustomer, idAccessWay, data,
  };
}
export function deleteInstallationCustomerAccessway(id, idCustomer, idAccessWay) {
  return {
    type: types.DELETE_INSTALLATION_CUSTOMER_ACCESSWAY, id, idCustomer, idAccessWay,
  };
}
export function triggerInstallationCustomerAccesswayRight(id, idCustomer, idAccessWay, idRight, name) {
  return {
    type: types.TRIGGER_INSTALLATION_CUSTOMER_ACCESSWAY_RIGHT, id, idCustomer, idAccessWay, idRight, cbParams: { customer: { id: idCustomer, name }, right: { id: idRight } },
  };
}
// Plannings
export function getInstallationPlannings(id, firstLoad) {
  return { type: types.GET_INSTALLATION_PLANNINGS, id, firstLoad };
}
export function getInstallationPlanning(id, idPlanning) {
  return { type: types.GET_INSTALLATION_PLANNING, id, idPlanning };
}
export function addInstallationPlanning(id, data) {
  return { type: types.ADD_INSTALLATION_PLANNING, id, data };
}
export function updateInstallationPlanning(id, idPlanning, data) {
  return {
    type: types.UPDATE_INSTALLATION_PLANNING, id, idPlanning, data,
  };
}
export function deleteInstallationPlanning(id, idPlanning) {
  return { type: types.DELETE_INSTALLATION_PLANNING, id, idPlanning };
}
// Logs
export function getInstallationLogsList(id, pagination, sort, filters, desynchronized) {
  return {
    type: types.GET_INSTALLATION_LOGS_LIST, id, pagination, sort, filters, desynchronized,
  };
}
// Async operations
export function getInstallationAsyncOperationsList(id, pagination, sort, filters, desynchronized) {
  return {
    type: types.GET_INSTALLATION_ASYNC_OPERATIONS_LIST, id, pagination, sort, filters, desynchronized, cbParams: { id },
  };
}
export function getInstallationAsyncOperationHistory(id, idAsyncOperation, pagination, sort, filters) {
  return {
    type: types.GET_INSTALLATION_ASYNC_OPERATION_HISTORY, id, idAsyncOperation, pagination, sort, filters,
  };
}

// Simcards / APNs
// Main
export function getSimcardsList(pagination, sort, filters, updateDeviceData = true) {
  return {
    type: types.GET_SIMCARDS_LIST, pagination, sort, filters, cbParams: { updateDeviceData },
  };
}
export function addSimcards(data) {
  return { type: types.ADD_SIMCARDS, data };
}
export function importSimcards(data) {
  return { type: types.IMPORT_SIMCARDS, data };
}
export function getSimcard(id, updateDeviceData = true) {
  return { type: types.GET_SIMCARD, id, cbParams: { updateDeviceData } };
}
export function updateSimcard(id, data) {
  return { type: types.UPDATE_SIMCARD, id, data };
}
export function deleteSimcard(id) {
  return { type: types.DELETE_SIMCARD, id };
}
export function synchronizeSimcards() {
  return { type: types.SYNCHRONIZE_SIMCARDS };
}
export function activateSimcards(data) {
  return { type: types.ACTIVATE_SIMCARDS, data };
}
// Devices
export function linkSimcardDevice(id, data, replacement) {
  return {
    type: types.LINK_SIMCARD_DEVICE, id, data, replacement,
  };
}
export function unlinkSimcardDevice(id, data) {
  return { type: types.UNLINK_SIMCARD_DEVICE, id, data };
}
// Groups
export function getSimcardGroupsList(pagination, sort, filters) {
  return {
    type: types.GET_SIMCARD_GROUPS_LIST, pagination, sort, filters,
  };
}
export function addSimcardGroup(data) {
  return { type: types.ADD_SIMCARD_GROUP, data };
}
export function getSimcardGroup(id) {
  return { type: types.GET_SIMCARD_GROUP, id };
}
export function addSimcardGroupApn(id, data) {
  return { type: types.ADD_SIMCARD_GROUP_APN, id, data };
}
export function deleteSimcardGroupApn(id, data) {
  return { type: types.DELETE_SIMCARD_GROUP_APN, id, data };
}
// APNs
export function getApnsList(pagination, sort, filters) {
  return {
    type: types.GET_APNS_LIST, pagination, sort, filters,
  };
}
export function addApn(data) {
  return { type: types.ADD_APN, data };
}

// Companies
// Main
export function getCompanies() {
  return { type: types.GET_COMPANIES };
}
export function addCompanySetParent(company) {
  return { type: types.ADD_COMPANY_SET_PARENT, company };
}
export function addCompany(data) {
  return { type: types.ADD_COMPANY, data };
}
export function getCompany(id) {
  return { type: types.GET_COMPANY, id };
}
export function updateCompany(id, data) {
  return { type: types.UPDATE_COMPANY, id, data };
}
export function deleteCompany(id) {
  return { type: types.DELETE_COMPANY, id };
}

// Users
// Main
export function getUsersList(pagination, sort, filters) {
  return {
    type: types.GET_USERS_LIST, pagination, sort, filters,
  };
}
export function addUser(data) {
  return { type: types.ADD_USER, data };
}
export function getUser(id) {
  return { type: types.GET_USER, id };
}
export function updateUser(id, data) {
  return { type: types.UPDATE_USER, id, data };
}
export function deleteUser(id) {
  return { type: types.DELETE_USER, id };
}
// Profiles
// Main
export function getProfilesList(pagination, sort, filters) {
  return {
    type: types.GET_PROFILES_LIST, pagination, sort, filters,
  };
}
export function addProfile(data) {
  return { type: types.ADD_PROFILE, data };
}
export function getProfile(id) {
  return { type: types.GET_PROFILE, id };
}
export function updateProfile(id, data) {
  return { type: types.UPDATE_PROFILE, id, data };
}
export function deleteProfile(id) {
  return { type: types.DELETE_PROFILE, id };
}
// Permissions
export function getPermissions() {
  return { type: types.GET_PERMISSIONS };
}
// Alertings
export function getAlertings() {
  return { type: types.GET_ALERTINGS };
}

// Production
// Device batches
export function getDeviceBatches() {
  return { type: types.GET_DEVICE_BATCHES };
}
export function setDevicePermissions(permissions) {
  return { type: types.SET_DEVICE_PERMISSIONS, permissions };
}
export function addDeviceBatch(data) {
  return { type: types.ADD_DEVICE_BATCH, data };
}
export function getDeviceBatch(id) {
  return { type: types.GET_DEVICE_BATCH, id };
}
export function updateDeviceBatch(id, data) {
  return { type: types.UPDATE_DEVICE_BATCH, id, data };
}
export function getDeviceBatchAuthor(id, idAuthor) {
  return { type: types.GET_DEVICE_BATCH_AUTHOR, id, idAuthor };
}
export function getDeviceBatchCommitter(id, idCommitter) {
  return { type: types.GET_DEVICE_BATCH_COMMITTER, id, idCommitter };
}
export function getDeviceBatchDistribution(id, distribution) {
  return { type: types.GET_DEVICE_BATCH_DISTRIBUTION, id, distribution };
}
export function getDeviceBatchArchive(id) {
  return { type: types.GET_DEVICE_BATCH_ARCHIVE, id };
}

// Updates
// Distributions
export function getDistributionsList(pagination, sort, filters) {
  return {
    type: types.GET_DISTRIBUTIONS_LIST, pagination, sort, filters,
  };
}
export function addDistribution(data, onUploadProgress) {
  return { type: types.ADD_DISTRIBUTION, data, onUploadProgress };
}
export function getDistribution(id) {
  return { type: types.GET_DISTRIBUTION, id };
}
export function updateDistribution(id, data) {
  return { type: types.UPDATE_DISTRIBUTION, id, data };
}
export function deleteDistribution(id) {
  return { type: types.DELETE_DISTRIBUTION, id };
}
export function getDistributionArchive(id) {
  return { type: types.GET_DISTRIBUTION_ARCHIVE, id };
}
// Rollouts
export function getRolloutsList(pagination, sort, filters) {
  return {
    type: types.GET_ROLLOUTS_LIST, pagination, sort, filters,
  };
}
export function addRollout(data) {
  return { type: types.ADD_ROLLOUT, data };
}
export function getRollout(id) {
  return { type: types.GET_ROLLOUT, id };
}
export function deleteRollout(id) {
  return { type: types.DELETE_ROLLOUT, id };
}
export function getRolloutDevicesList(id, pagination, sort, filters) {
  return {
    type: types.GET_ROLLOUT_DEVICES_LIST, id, pagination, sort, filters,
  };
}

// From theme
export function toggleBoxedLayout(isLayoutBoxed) {
  return { type: types.TOGGLE_BOXED_LAYOUT, isLayoutBoxed };
}
export function toggleCollapsedNav(isNavCollapsed) {
  return { type: types.TOGGLE_COLLAPSED_NAV, isNavCollapsed };
}
export function toggleNavBehind(isNavBehind) {
  return { type: types.TOGGLE_NAV_BEHIND, isNavBehind };
}
export function toggleFixedHeader(isFixedHeader) {
  return { type: types.TOGGLE_FIXED_HEADER, isFixedHeader };
}
export function changeSidebarWidth(sidebarWidth) {
  return { type: types.CHANGE_SIDEBAR_WIDTH, sidebarWidth };
}
export function changeColorOption(colorOption) {
  return { type: types.CHANGE_COLOR_OPTION, colorOption };
}
export function changeTheme(themeOption) {
  return { type: types.CHANGE_THEME, theme: themeOption };
}

// ---------------------------------------------------------- //
// --------------- Car park management actions -------------- //
// ---------------------------------------------------------- //

// Operations
export function cpmRegisterOperation(operation) {
  return { type: types.CPM_REGISTER_OPERATION, operation };
}
export function cpmPurgeOperations(targetKey) {
  return { type: types.CPM_PURGE_OPERATIONS, targetKey };
}
export function cpmUpdateStatusOperations(status, error) {
  return { type: types.CPM_UPDATE_STATUS_OPERATIONS, status, error };
}

// Events
export function cpmUpdateEventStatus(id, status) {
  return { type: types.CPM_UPDATE_EVENT_STATUS, id, status };
}

// Registrations
export function cpmRegisterCarparkTrigger(resource, operationId) {
  return { type: types.CPM_REGISTER_CARPARK_TRIGGER, resource, operationId };
}

// AutoComplete
export function cpmGetCarparksAutoComplete(query, data, listParams) {
  return {
    type: types.CPM_GET_CARPARKS_AUTOCOMPLETE, query, data, listParams,
  };
}

export function cpmGetCarparkCustomersAutocomplete(query, data, listParams) {
  return {
    type: types.CPM_GET_CARPARK_CUSTOMERS_AUTOCOMPLETE, query, data, listParams,
  };
}
export function cpmGetCarparkParkingsAutocomplete(query, data, listParams) {
  return {
    type: types.CPM_GET_CARPARK_PARKINGS_AUTOCOMPLETE, query, data, listParams,
  };
}

// List filter
export function cpmGetCarparkCustomerLF(query, data) {
  return { type: types.CPM_GET_CARPARK_CUSTOMER_LF, query, data };
}
export function cpmGetCarparkParkingLF(query, data) {
  return { type: types.CPM_GET_CARPARK_PARKING_LF, query, data };
}

// Price rules
export function cpmGetPriceRules(query, listParams) {
  return { type: types.CPM_GET_PRICE_RULES, query, listParams };
}

// Car parks
// Main
export function cpmGetCarparksList(query, data, listParams, count) {
  return {
    type: types.CPM_GET_CARPARKS_LIST, query, data, listParams, cbParams: { count },
  };
}

export function cpmCountCarparks(query, data, listParams, count) {
  return {
    type: types.CPM_COUNT_CARPARKS, query, data, listParams, cbParams: { count },
  };
}

export function cpmGetCarpark(query, data) {
  return { type: types.CPM_GET_CARPARK, query, data };
}
export function cpmGetCarparkSynchronizationStatuses(query, data) {
  return { type: types.CPM_GET_CARPARK_SYNCHRONIZATION_STATUSES, query, data };
}
export function cpmUpdateCarpark(query, data) {
  return { type: types.CPM_UPDATE_CARPARK, query, data };
}
export function cpmUpdateCarparkContact(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_CONTACT, query, data };
}
export function cpmTriggerCarparkDoor(query, data, door) {
  return {
    type: types.CPM_TRIGGER_CARPARK_DOOR, query, data, cbParams: { door },
  };
}
export function cpmGetCarparkUniqueKeypadCode(query, data) {
  return { type: types.CPM_GET_CARPARK_UNIQUE_KEYPAD_CODE, query, data };
}
export function cpmGetCarparkRevenuesSummary(query, data, scope) {
  return {
    type: types.CPM_GET_CARPARK_REVENUES_SUMMARY, query, data, cbParams: { scope },
  };
}
export function cpmUpdateCarparkBookingSettings(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_BOOKING_SETTINGS, query, data };
}
export function cpmUpdateCarparkGeneralSettings(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_GENERAL_SETTINGS, query, data };
}
export function cpmUpdateCarparkOnDemandSettings(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_ON_DEMAND_SETTINGS, query, data };
}
export function cpmUpdateCarparkLicensePlateSettings(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_LICENSE_PLATE_SETTINGS, query, data };
}
export function cpmUpdateCarparkSpacesSettings(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_SPACES_SETTINGS, query, data };
}
export function cpmUpdateCarparkFiancialInformation(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_FINANCIAL_INFORMATION, query, data };
}
export function cpmGetCarparkBySlug(query, data) {
  return { type: types.CPM_GET_CARPARK_BY_SLUG, query, data };
}
export function cpmGetCarparkDefaultSlug(query, data) {
  return { type: types.CPM_GET_CARPARK_DEFAULT_SLUG, query, data };
}
export function cpmUpdateCarparkWorkspaceGroups(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_WORKSPACE_GROUPS, query, data };
}
export function cpmGetCarparkWorkspaceGroups(query, data, listParams) {
  return {
    type: types.CPM_GET_CARPARK_WORKSPACE_GROUPS, query, data, listParams,
  };
}
export function cpmUpdateCarparkPartner(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_PARTNER, query, data };
}

// Activity
export function cpmGetCarparkActivityArrivalsList(query, data, listParams) {
  return {
    type: types.CPM_GET_CARPARK_ACTIVITY_ARRIVALS_LIST, query, data, listParams,
  };
}
export function cpmGetCarparkActivityDeparturesList(query, data, listParams) {
  return {
    type: types.CPM_GET_CARPARK_ACTIVITY_DEPARTURES_LIST, query, data, listParams,
  };
}
export function cpmGetCarparkActivityEntriesList(query, data, listParams) {
  return {
    type: types.CPM_GET_CARPARK_ACTIVITY_ENTRIES_LIST, query, data, listParams,
  };
}
export function cpmGetCarparkActivityExitsList(query, data, listParams) {
  return {
    type: types.CPM_GET_CARPARK_ACTIVITY_EXITS_LIST, query, data, listParams,
  };
}
// Plannings
export function cpmGetCarparkPlannings(query, data, listParams, firstLoad) {
  return {
    type: types.CPM_GET_CARPARK_PLANNINGS, query, data, listParams, firstLoad,
  };
}
export function cpmGetCarparkPlanning(query, data) {
  return { type: types.CPM_GET_CARPARK_PLANNING, query, data };
}
export function cpmAddCarparkPlanning(query, data) {
  return { type: types.CPM_ADD_CARPARK_PLANNING, query, data };
}
export function cpmUpdateCarparkPlanning(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_PLANNING, query, data };
}
export function cpmDeleteCarparkPlanning(query, data) {
  return { type: types.CPM_DELETE_CARPARK_PLANNING, query, data };
}
// Parking categories
export function cpmGetCarparkParkingCategories(query, data, listParams, firstLoad) {
  return {
    type: types.CPM_GET_CARPARK_PARKING_CATEGORIES, query, data, listParams, firstLoad,
  };
}
export function cpmAddCarparkParkingCategory(query, data) {
  return { type: types.CPM_ADD_CARPARK_PARKING_CATEGORY, query, data };
}
export function cpmUpdateCarparkParkingCategory(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_PARKING_CATEGORY, query, data };
}
export function cpmDeleteCarparkParkingCategory(query, data) {
  return { type: types.CPM_DELETE_CARPARK_PARKING_CATEGORY, query, data };
}
export function cpmAddCarparkPriceGridToParkingCategory(query, data, parkingCategoryId) {
  return {
    type: types.CPM_ADD_CARPARK_PRICE_GRID_TO_PARKING_CATEGORY, query, data, cbParams: { parkingCategoryId },
  };
}
export function cpmDeleteCarparkPriceGridFromParkingCategory(query, data, parkingCategoryId) {
  return {
    type: types.CPM_DELETE_CARPARK_PRICE_GRID_FROM_PARKING_CATEGORY, query, data, cbParams: { parkingCategoryId },
  };
}
export function cpmSendCarparkPriceGridToCustomer(query, data) {
  return { type: types.CPM_SEND_CARPARK_PRICE_GRID_TO_CUSTOMER, query, data };
}
// Price grids
export function cpmGetCarparkPriceGrids(query, data, listParams, firstLoad) {
  return {
    type: types.CPM_GET_CARPARK_PRICE_GRIDS, query, data, listParams, firstLoad,
  };
}
export function cpmAddCarparkPriceGrid(query, data) {
  return { type: types.CPM_ADD_CARPARK_PRICE_GRID, query, data };
}
export function cpmUpdateCarparkPriceGrid(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_PRICE_GRID, query, data };
}
// Customers
export function cpmGetCarparkCustomersList(query, data, listParams) {
  return {
    type: types.CPM_GET_CARPARK_CUSTOMERS_LIST, query, data, listParams,
  };
}
export function cpmGetCarparkCustomer(query, data) {
  return { type: types.CPM_GET_CARPARK_CUSTOMER, query, data };
}
export function cpmAddCarparkCustomer(query, data) {
  return { type: types.CPM_ADD_CARPARK_CUSTOMER, query, data };
}
export function cpmUpdateCarparkCustomer(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_CUSTOMER, query, data };
}
export function cpmDeleteCarparkCustomer(query, data) {
  return { type: types.CPM_DELETE_CARPARK_CUSTOMER, query, data };
}
export function cpmCreateCarparkCustomerAccount(query, data) {
  return { type: types.CPM_CREATE_CARPARK_CUSTOMER_ACCOUNT, query, data };
}
export function cpmDeleteCarparkCustomerAccount(query, data) {
  return { type: types.CPM_DELETE_CARPARK_CUSTOMER_ACCOUNT, query, data };
}
export function cpmGetCarparkCustomerParkingsList(query, data, listParams, customerId) {
  return {
    type: types.CPM_GET_CARPARK_CUSTOMER_PARKINGS_LIST, query, data, listParams, cbParams: { customerId },
  };
}
export function cpmGetCarparkCustomerBookingsList(query, data, listParams, customerId) {
  return {
    type: types.CPM_GET_CARPARK_CUSTOMER_BOOKINGS_LIST, query, data, listParams, cbParams: { customerId },
  };
}
export function cpmGetCarparkCustomerOldParkingsList(query, data, listParams, customerId) {
  return {
    type: types.CPM_GET_CARPARK_CUSTOMER_OLD_PARKINGS_LIST, query, data, listParams, cbParams: { customerId },
  };
}
export function cpmGetCarparkCustomerAccessesList(query, data, listParams, customerId) {
  return {
    type: types.CPM_GET_CARPARK_CUSTOMER_ACCESSES_LIST, query, data, listParams, cbParams: { customerId },
  };
}
// Parkings
export function cpmGetCarparkParkingsList(query, data, listParams, firstLoad) {
  return {
    type: types.CPM_GET_CARPARK_PARKINGS_LIST, query, data, listParams, firstLoad,
  };
}
export function cpmAddCarparkParking(query, data, parkingType) {
  return {
    type: types.CPM_ADD_CARPARK_PARKING, query, data, cbParams: { parkingType },
  };
}
export function cpmUpdateCarparkParking(query, data, parkingType) {
  return {
    type: types.CPM_UPDATE_CARPARK_PARKING, query, data, cbParams: { parkingType },
  };
}
export function cpmUpdateCarparkParkingState(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_PARKING_STATE, query, data };
}
export function cpmDeleteCarparkParking(query, data, parkingType) {
  return {
    type: types.CPM_DELETE_CARPARK_PARKING, query, data, cbParams: { parkingType },
  };
}
export function cpmGetCarparkParkingAccessesList(query, data, listParams, parkingId) {
  return {
    type: types.CPM_GET_CARPARK_PARKING_ACCESSES_LIST, query, data, listParams, cbParams: { parkingId },
  };
}
// Booking
export function cpmGetCarparkBookingsList(query, data, listParams) {
  return {
    type: types.CPM_GET_CARPARK_BOOKINGS_LIST, query, data, listParams,
  };
}
export function cpmAddCarparkBooking(query, data) {
  return {
    type: types.CPM_ADD_CARPARK_BOOKING, query, data,
  };
}
export function cpmUpdateCarparkBooking(query, data) {
  return {
    type: types.CPM_UPDATE_CARPARK_BOOKING, query, data,
  };
}
export function cpmUpdateCarparkBookingState(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_BOOKING_STATE, query, data };
}
export function cpmSendBookingPaymentLink(query, data) {
  return { type: types.CPM_SEND_BOOKING_PAYMENT_LINK, query, data };
}

export function cpmRefundBooking(query, data) {
  return { type: types.CPM_REFUND_BOOKING, query, data };
}

export function cpmRefundPurchase(query, data) {
  return { type: types.CPM_REFUND_PURCHASE, query, data };
}

export function cpmRefundOnDemandAccess(query, data) {
  return { type: types.CPM_REFUND_ON_DEMAND_ACCESS, query, data };
}

export function cpmCancelCarparkBooking(query, data) {
  return {
    type: types.CPM_CANCEL_CARPARK_BOOKING, query, data,
  };
}
export function cpmGetOneparkBookingsList(query, data, listParams) {
  return {
    type: types.CPM_GET_ONEPARK_BOOKINGS_LIST, query, data, listParams,
  };
}

export function cpmResendBookingConfirmationEmail(query, data) {
  return { type: types.CPM_RESEND_BOOKING_CONFIRMATION_EMAIL, query, data };
}

// On demand
export function cpmGetCarparkOnDemandList(query, data, listParams, firstLoad) {
  return {
    type: types.CPM_GET_CARPARK_ON_DEMAND_LIST, query, data, listParams, firstLoad,
  };
}
export function cpmCategorizeCarparkOnDemand(query, data, parkingId) {
  return {
    type: types.CPM_CATEGORIZE_CARPARK_ON_DEMAND, query, data, cbParams: { parkingId },
  };
}
export function cpmDeleteCarparkOnDemand(query, data) {
  return { type: types.CPM_DELETE_CARPARK_ON_DEMAND, query, data };
}

export function cpmUpdateCarparkOnDemandAccessState(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_ON_DEMAND_ACCESS_STATE, query, data };
}

export function cpmDeclareFinalExitForTemporaryOnDemand(query, data) {
  return { type: types.CPM_DECLARE_FINAL_EXIT_FOR_TEMPORARY_ON_DEMAND_ACCESS, query, data };
}

export function cpmDeclareFinalExitForBooking(query, data) {
  return { type: types.CPM_DECLARE_FINAL_EXIT_FOR_BOOKING, query, data };
}

export function cpmGetOnDemandAccess(query, data) {
  return { type: types.CPM_GET_ON_DEMAND_ACCESS, query, data };
}
export function cpmSearchOnDemandAccess(query, data) {
  return { type: types.CPM_SEARCH_ON_DEMAND_ACCESS, query, data };
}
export function cpmSelfCheckoutOnDemandAccess(query, data) {
  return { type: types.CPM_SELF_CHECKOUT_ON_DEMAND_ACCESS, query, data };
}
export function cpmExtendOnDemandAccess(query, data) {
  return { type: types.CPM_EXTEND_ON_DEMAND_ACCESS, query, data };
}
export function cpmPayOnDemandAccessOverstay(query, data) {
  return { type: types.CPM_PAY_ON_DEMAND_ACCESS_OVERSTAY, query, data };
}
export function cpmCreateOnDemandAccess(query, data) {
  return { type: types.CPM_CREATE_ON_DEMAND_ACCESS, query, data };
}
export function cpmTerminateOnDemandAccess(query, data) {
  return { type: types.CPM_TERMINATE_ON_DEMAND_ACCESS, query, data };
}
export function cpmUpdateOnDemandAccessCustomer(query, data) {
  return { type: types.CPM_UPDATE_ON_DEMAND_ACCESS_CUSTOMER, query, data };
}
export function cpmUpdateOnDemandAccess(query, data) {
  return { type: types.CPM_UPDATE_ON_DEMAND_ACCESS, query, data };
}

// Accesses
export function cpmGetCarparkAccessesList(query, data, listParams) {
  return {
    type: types.CPM_GET_CARPARK_ACCESSES_LIST, query, data, listParams,
  };
}
// Metrics
export function cpmGetCarparkMetricsOccupancyRateLive(query, data, parkingCategoryId) {
  return {
    type: types.CPM_GET_CARPARK_METRICS_OCCUPANCY_RATE_LIVE, query, data, cbParams: { parkingCategoryId },
  };
}
export function cpmGetCarparkMetricsOccupancyRatePeriod(query, data, parkingCategoryId) {
  return {
    type: types.CPM_GET_CARPARK_METRICS_OCCUPANCY_RATE_PERIOD, query, data, cbParams: { parkingCategoryId },
  };
}
export function cpmGetCarparkMetricsOccupancyRatePredicted(query, data, parkingCategoryId) {
  return {
    type: types.CPM_GET_CARPARK_METRICS_OCCUPANCY_RATE_PREDICTED, query, data, cbParams: { parkingCategoryId },
  };
}
export function cpmGetCarparkMetricsAccesses(query, data, parkingCategoryId) {
  return {
    type: types.CPM_GET_CARPARK_METRICS_ACCESSES, query, data, cbParams: { parkingCategoryId },
  };
}
export function cpmGetCarparkMetricsRevenues(query, data, parkingCategoryId) {
  return {
    type: types.CPM_GET_CARPARK_METRICS_REVENUES, query, data, cbParams: { parkingCategoryId },
  };
}

export function cpmGetCarparkMetricsRevenuesReport(query, data) {
  return {
    type: types.CPM_GET_CARPARK_METRICS_REVENUES_REPORT, query, data,
  };
}

// My Bookings
export function cpmListMyBookings(query, listParams) {
  return {
    type: types.CPM_GET_MY_BOOKINGS_LIST, query, listParams,
  };
}
export function cpmListMyCarparkBookingOptions(query, data) {
  return {
    type: types.CPM_LIST_MY_CARPARK_BOOKING_OPTIONS, query, data,
  };
}
export function cpmSearchCarparkBookingOffer(query, data) {
  return {
    type: types.CPM_SEARCH_CARPARK_BOOKING_OFFER, query, data,
  };
}
export function cpmSearchCarparkBookingExtensionOffer(query, data) {
  return {
    type: types.CPM_SEARCH_CARPARK_BOOKING_EXTENSION_OFFER, query, data,
  };
}
export function cpmBookCarpark(query, data) {
  return {
    type: types.CPM_BOOK_CARPARK, query, data,
  };
}
export function cpmExtendBookCarpark(query, data) {
  return {
    type: types.CPM_EXTEND_BOOK_CARPARK, query, data,
  };
}
export function cpmPayBookingOverstay(query, data) {
  return {
    type: types.CPM_PAY_BOOKING_OVERSTAY, query, data,
  };
}
export function cpmCancelMyBooking(query, data) {
  return {
    type: types.CPM_CANCEL_MY_BOOKING, query, data,
  };
}
export function cpmGetMyBooking(query, data) {
  return {
    type: types.CPM_GET_MY_BOOKING, query, data,
  };
}
export function cpmGetUpdatedMyBooking(query, data) {
  return {
    type: types.CPM_GET_UPDATED_MY_BOOKING, query, data,
  };
}
export function cpmGetBookingOffer(query, data) {
  return {
    type: types.CPM_GET_OFFER, query, data,
  };
}

// Sidebar
export function cpmToggleSidebar() {
  return { type: types.CPM_TOGGLE_SIDEBAR };
}

// Accounts
export function cpmSignUp(query, data) {
  return { type: types.CPM_SIGN_UP, query, data };
}
export function cpmListAccounts(query, listParams) {
  return {
    type: types.CPM_GET_ACCOUNTS_LIST, query, listParams,
  };
}
export function cpmSetAccountStatus(query, data) {
  return { type: types.CPM_SET_ACCOUNT_STATUS, query, data };
}
export function cpmCheckCustomerAccountLink(query, data) {
  return { type: types.CPM_CHECK_CUSTOMER_ACCOUNT_LINK, query, data };
}
export function cpmValidateCustomerAccountLink(query, data) {
  return { type: types.CPM_VALIDATE_CUSTOMER_ACCOUNT_LINK, query, data };
}
export function cpmValidateAccount(query, data) {
  return { type: types.CPM_VALIDATE_ACCOUNT_LINK, query, data };
}
// Invoices
export function cpmListCarparkInvoices(query, data, listParams) {
  return {
    type: types.CPM_GET_CARPARK_INVOICES_LIST, query, data, listParams,
  };
}

export function cpmDownloadCarparkInvoice(query, data) {
  return { type: types.CPM_DOWNLOAD_CARPARK_INVOICE, query, data };
}

export function cpmDownloadCarparkInvoices(query, data, listParams) {
  return {
    type: types.CPM_DOWNLOAD_CARPARK_INVOICES, query, data, listParams,
  };
}

export function cpmDownloadMyInvoice(query, data) {
  return { type: types.CPM_DOWNLOAD_MY_INVOICE, query, data };
}

export function cpmUpdateCarparkInvoice(query, data) {
  return { type: types.CPM_UPDATE_CARPARK_INVOICE, query, data };
}

export function cpmResetMyInvoiceDownloadRequest() {
  return { type: types.CPM_RESET_MY_INVOICE_DOWNLOAD_REQUEST };
}

export function cpmResetCarparkInvoiceDownloadRequest() {
  return { type: types.CPM_RESET_CARPARK_INVOICE_DOWNLOAD_REQUEST };
}

// Partners
export function cpmGetPartnersList(query, data, listParams) {
  return {
    type: types.CPM_GET_PARTNERS_LIST, query, data, listParams,
  };
}
export function cpmCreatePartner(query, data) {
  return {
    type: types.CPM_CREATE_PARTNER, query, data,
  };
}
export function cpmUpdatePartnerFinancialInformation(query, data) {
  return {
    type: types.CPM_UPDATE_PARTNER_FINANCIAL_INFORMATION, query, data,
  };
}

// Workspaces
export function cpmListWorkspaces(query, listParams) {
  return {
    type: types.CPM_LIST_WORKSPACES, query, listParams,
  };
}
export function cpmGetWorkspace(query, data) {
  return { type: types.CPM_GET_WORKSPACE, query, data };
}
export function cpmCreateWorkspace(query, data) {
  return { type: types.CPM_CREATE_WORKSPACE, query, data };
}
export function cpmUpdateWorkspace(query, data) {
  return { type: types.CPM_UPDATE_WORKSPACE, query, data };
}
export function cpmListWorkspaceGroups(query, data, listParams) {
  return {
    type: types.CPM_LIST_WORKSPACE_GROUPS, query, data, listParams,
  };
}
export function cpmGetWorkspaceGroup(query, data) {
  return { type: types.CPM_GET_WORKSPACE_GROUP, query, data };
}
export function cpmCreateWorkspaceGroup(query, data) {
  return { type: types.CPM_CREATE_WORKSPACE_GROUP, query, data };
}
export function cpmUpdateWorkspaceGroup(query, data) {
  return { type: types.CPM_UPDATE_WORKSPACE_GROUP, query, data };
}

// Service fee rules
export function cpmGetServiceFeeRule(query, data, carparksListParams) {
  return {
    type: types.CPM_GET_SERVICE_FEE_RULE, query, data, carparksListParams,
  };
}
export function cpmListServiceFeeRules(query, data, listParams) {
  return {
    type: types.CPM_LIST_SERVICE_FEE_RULES, query, data, listParams,
  };
}
export function cpmCreateServiceFeeRule(query, data) {
  return { type: types.CPM_CREATE_SERVICE_FEE_RULE, query, data };
}
export function cpmDeleteServiceFeeRule(query, data) {
  return { type: types.CPM_DELETE_SERVICE_FEE_RULE, query, data };
}
export function cpmUpdateServiceFeeRule(query, data) {
  return { type: types.CPM_UPDATE_SERVICE_FEE_RULE, query, data };
}

// Slugs
export function cpmListSlugs(query, listParams) {
  return {
    type: types.CPM_LIST_SLUGS, query, listParams,
  };
}
export function cpmGetSlugByDomain(query, data) {
  return {
    type: types.CPM_GET_SLUG_BY_DOMAIN, query, data,
  };
}
export function cpmCreateSlug(query, data) {
  return { type: types.CPM_CREATE_SLUG, query, data };
}
export function cpmDeleteSlug(query, data) {
  return { type: types.CPM_DELETE_SLUG, query, data };
}
export function cpmUpdateSlug(query, data) {
  return { type: types.CPM_UPDATE_SLUG, query, data };
}

export function cpmInitiatePayment(query, data) {
  return {
    type: types.CPM_INITIATE_PAYMENT, query, data,
  };
}



// WEBPACK FOOTER //
// ./actions/index.js